import { Field } from "formik";
import { ChangeEvent, FocusEvent, useMemo, useState } from "react";
import { Accordion, Button, Col, Row } from "react-bootstrap";
import { CensusCategory } from "../../../types/Census/CensusCategory";
import { CensusFormData } from "./Census";
import { CensusAccordionHeader } from "./CensusAccordionHeader";

interface AgeBandSelectionProps {
  onAgeBandSelection: (ageBandFrom: number, ageBandTo: number, values: CensusFormData) => void;
  values: CensusFormData;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void;
  ageBandCategory: CensusCategory | undefined;
}

const AgeBandSelection = ({ onAgeBandSelection, values, setFieldValue, ageBandCategory }: AgeBandSelectionProps) => {
  const [ageBandFrom, setAgeBandFrom] = useState<number>(0);
  const [ageBandTo, setAgeBandTo] = useState<number>(0);

  const maxAge = useMemo(() => {
    if (ageBandCategory) {
      return ageBandCategory.columns.reduce((max, column) => {
        if (column.ageBandValue) {
          return Math.max(max, column.ageBandValue);
        }
        return max;
      }, 0);
    }
    return 0;
  }, [ageBandCategory]);

  const handleFromChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? '' : parseInt(e.target.value);
    if (value === '' || (typeof value === 'number' && value <= maxAge)) {
      setAgeBandFrom(value as number);
    }
  };

  const handleToChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? '' : parseInt(e.target.value);
    if (value === '' || (typeof value === 'number' && value <= maxAge)) {
      setAgeBandTo(value as number);
    }
  };

  const handleFromBlur = (e: FocusEvent<HTMLInputElement>) => {
    let value = e.target.value === '' || isNaN(parseInt(e.target.value))
      ? 0
      : parseInt(e.target.value);
    
    value = Math.min(value, maxAge);
    
    setAgeBandFrom(value);
    
    if (value > ageBandTo) {
      setAgeBandTo(value);
    }
  };

  const handleToBlur = (e: FocusEvent<HTMLInputElement>) => {
    let value = e.target.value === '' || isNaN(parseInt(e.target.value))
      ? 0
      : parseInt(e.target.value);
    
    value = Math.min(value, maxAge);
    
    setAgeBandTo(value);
    
    if (value < ageBandFrom) {
      setAgeBandFrom(value);
    }
  };

  const handleAddClick = () => {
    onAgeBandSelection(ageBandFrom, ageBandTo, values);
    setFieldValue('', '');
  };

  if(!ageBandCategory) {
    return <></>
  }

  return (
    <Accordion.Item eventKey="1">
      <CensusAccordionHeader>Age Band Selection</CensusAccordionHeader>
      <Accordion.Body>
        <Row className="mb-3">
          <Col>
            <Field
              type="number"
              name="ageBandFrom"
              min="0"
              max={maxAge}
              value={ageBandFrom}
              onChange={handleFromChange}
              onBlur={handleFromBlur}
              className="form-control"
            />
          </Col>
          <Col xs="auto" className="d-flex align-items-center">
            to
          </Col>
          <Col>
            <Field
              type="number"
              name="ageBandTo"
              min="0"
              max={maxAge}
              value={ageBandTo}
              onChange={handleToChange}
              onBlur={handleToBlur}
              className="form-control"
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Button 
              variant="info" 
              onClick={handleAddClick}
            >
              Add
            </Button>
          </Col>
        </Row>
      </Accordion.Body>
    </Accordion.Item>
  );
};

export default AgeBandSelection;